import api from '@/services/Api';
import Untersuchung from '@/types/Untersuchung';
import { successSwal, warningSwal } from '@/helper/swals';
import { ActionContext, Module } from 'vuex';
import Untersuchungsbefundvariante from '@/types/Untersuchungsbefundvariante';
import { AxiosResponse } from 'axios';
import { RootState, UntersuchungenState } from '@/store/types';
import { DataResponse } from '@/types';

export type UntersuchungenFilter = { impfstelle_id?: number; gueltig?: string };
export type LoadUntersuchungenData = DataResponse<Partial<Untersuchung>[], {
    befundvarianten: Partial<Untersuchungsbefundvariante[]>
}>;
export type LoadUntersuchungenPayload = { filter: UntersuchungenFilter };
export type UpdateUntersuchungPayload = { untersuchung: Untersuchung; filter: UntersuchungenFilter };
export type DeleteUntersuchungPayload = { untersuchung: Untersuchung; filter: UntersuchungenFilter };
export type SetUntersuchungenDialogs = { edit?: boolean };
export type SetUntersuchungenLoaders = { save?: boolean; delete?: boolean; untersuchungen?: boolean };

type Context = ActionContext<UntersuchungenState, RootState>;

export const untersuchungen: Module<UntersuchungenState, RootState> = {
    namespaced: true,
    state: {
        dialogs: {
            edit: false,
        },
        loaders: {
            save: false,
            delete: false,
            untersuchungen: false,
        },
        untersuchungen: [],
        befundvarianten: [],
    },
    actions: {
        loadUntersuchungen({ commit }: Context, payload?: LoadUntersuchungenPayload): void {
            commit('setLoaders', { untersuchungen: true });

            const data = {
                ...payload,
            };

            api.post('verwaltung/untersuchungen/load', data)
                .then((response) => commit('setUntersuchungen', response.data))
                .catch(console.error)
                .finally(() => commit('setLoaders', { untersuchungen: false }));
        },
        updateUntersuchung({ commit, dispatch, rootState }: Context,
                           { untersuchung, filter }: UpdateUntersuchungPayload): void {
            commit('setLoaders', { save: true });

            const data = {
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...untersuchung,
            };

            api.post('verwaltung/untersuchungen/speichern', data)
                .then(() => {
                    successSwal('Untersuchung', 'gespeichert');
                    dispatch('loadUntersuchungen', { filter });
                    commit('setDialogs', { edit: false });
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { save: false }));
        },
        deleteUntersuchung({ commit, dispatch, rootState }: Context,
                           { untersuchung: { untersuchung_id }, filter }: DeleteUntersuchungPayload): void {
            commit('setLoaders', { delete: true });

            const data = {
                impfstelle_id: rootState.authentication.impfstelle_id,
                untersuchung_id,
            };

            api.post('verwaltung/untersuchungen/loeschen', data)
                .then((response: AxiosResponse) => {
                    if (typeof response.data.body === 'string') {
                        warningSwal('Löschung', response.data.body ?? 'Untersuchung ist nicht löschbar.');
                    } else {
                        successSwal('Untersuchung', 'gelöscht');
                        dispatch('loadUntersuchungen', { filter });
                        commit('setDialogs', { edit: false });
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { delete: false }));
        },
    },
    mutations: {
        setUntersuchungen(state: UntersuchungenState, { body, selections }: LoadUntersuchungenData): void {
            state.untersuchungen = body.map((data) => new Untersuchung(data));
            state.befundvarianten = selections.befundvarianten.map((data) => new Untersuchungsbefundvariante(data));
        },
        setDialogs(state: UntersuchungenState, dialogs: SetUntersuchungenDialogs): void {
            state.dialogs = {
                ...state.dialogs,
                ...dialogs,
            };
        },
        setLoaders(state: UntersuchungenState, loaders: SetUntersuchungenLoaders): void {
            state.loaders = {
                ...state.loaders,
                ...loaders,
            };
        },
    },
};
